import './ExchangeCredentialsModal.scss'
import React from 'react'
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ReactComponent as ZocksLogo } from '../../../../../assets/images/ZocksIcon-white.svg'
import { useCreateUserIntegrationMutation } from '../../../../../store/user/user'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { addToast } from '../../../../../store/toast/toastSlice'
import { createState, getIntegrationShortDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { getIntegrationEnableUrl, getIntegrationRedirectUri } from '../../../utils'

export type ExchangeCredentialsFormValues = {
  serverUrl: string
  useOauth: boolean
  username?: string
  password?: string
}

export type ExchangeServiceId = IntegrationServiceId.EXCHANGE_CALENDAR | IntegrationServiceId.EXCHANGE_MAIL

export interface ExchangeCredentialsModalProps {
  serviceId: ExchangeServiceId
  show: boolean
  onSave: () => void
  onCancel: () => void
  postConnectRedirectPath?: string
}

export const ExchangeCredentialsModal: React.FC<ExchangeCredentialsModalProps> = ({ serviceId, show, onSave, onCancel, postConnectRedirectPath }) => {
  const defaultValues = {
    serverUrl: '',
    useOauth: false,
    username: '',
    password: '',
  }

  const form = useForm<ExchangeCredentialsFormValues>({ defaultValues })
  const [createUserIntegration, { isLoading: isCreatingIntegration }] = useCreateUserIntegrationMutation()
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const { handleSubmit } = form

  const handleConnect = async (data: ExchangeCredentialsFormValues) => {
    const serverUrl = data.serverUrl.trim()
    if (data.useOauth) {
      const redirectUri = getIntegrationRedirectUri(serviceId)
      const postConnectRedirectOrDefaultUri = new URL(postConnectRedirectPath ?? '/dashboard/account/integration-settings/integrations', window.location.origin)
        .href
      const state = createState(postConnectRedirectOrDefaultUri, null, serverUrl)
      window.location.assign(getIntegrationEnableUrl(accountId, serviceId, redirectUri, state))
      return
    }
    try {
      await createUserIntegration({
        accountId,
        integrationName: serviceId,
        payload: {
          serverUrl,
          username: data.username ?? '',
          password: data.password ?? '',
        },
      }).unwrap()
      onSave()
    } catch (e) {
      console.error(e)
      dispatch(
        addToast({
          variant: 'error',
          title: 'Failed to create integration',
          messages: ['Unable to connect to Exchange. Please check your credentials and try again.'],
          delay: 10000,
        }),
      )
    }
  }

  const handleOnHide = () => {
    form.reset()
    onCancel()
  }

  const body = (
    <Container>
      <Row className='zocks-pb-2'>
        <Stack direction='horizontal' className='d-flex justify-content-center align-items-center' gap={3}>
          <div className='zocks-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <ZocksLogo width='3rem' height='3rem' />
          </div>
          <i className='bi bi-arrow-left-right'></i>
          <div className='exchange-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <IntegrationIcon serviceId={serviceId} fill='white' width='3rem' height='3rem' />
          </div>
        </Stack>
      </Row>
      <Row className='pb-3'>
        <Col>
          <h3 className='d-flex justify-content-center align-items-center'>Connect {getIntegrationShortDisplayName(serviceId)}</h3>
        </Col>
      </Row>
      <Row className='pb-3'>
        <Col>
          <p className='d-flex justify-content-center align-items-center'>
            For Exchange running on on-premise servers (e.g. Smarsh). For Microsoft 365, connect Outlook instead.
          </p>
        </Col>
      </Row>

      <Row>
        <FormProvider {...form}>
          <ExchangeCredentialsForm />
        </FormProvider>
      </Row>
    </Container>
  )

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onCancel}>
        Cancel
      </Button>
      <Button variant='primary' onClick={handleSubmit(handleConnect)} loading={isCreatingIntegration}>
        Connect
      </Button>
    </div>
  )

  return <Modal show={show} onHide={handleOnHide} header={<></>} body={body} footer={footer} centered={true} />
}

const ExchangeCredentialsForm: React.FC = () => {
  const { control } = useFormContext<ExchangeCredentialsFormValues>()
  const useOauth = useWatch({ control, name: 'useOauth' })

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col>
          <Controller
            name='serverUrl'
            control={control}
            rules={{
              required: 'Exchange server url is required.',
              validate: (value) => !!value.trim() || 'Exchange server url is required.',
            }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>
                  Exchange Login URL
                  <Tooltip
                    className='zocks-text-gray-500'
                    placement='right'
                    title='Please enter the server address make sure to include https:// and without the /owa suffix. Example: https://mail.company.com'
                  >
                    <i className='bi bi-info-circle' />
                  </Tooltip>
                </Form.Label>
                <Form.Control id={field.name} type='text' placeholder='Enter exchange login url' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name='useOauth'
            control={control}
            render={({ field }) => (
              <Form.Check
                id={field.name}
                type='checkbox'
                label='Sign in with Microsoft (OAuth)'
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            )}
          />
        </Col>
      </Row>
      {useOauth ? null : (
        <>
          <Row>
            <Col>
              <Controller
                name='username'
                control={control}
                shouldUnregister
                rules={{
                  required: 'Email address is required.',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Email address</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter email address' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='password'
                control={control}
                shouldUnregister
                rules={{
                  required: 'Password is required.',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Password</Form.Label>
                    <Form.Control id={field.name} type='password' placeholder='Enter password…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
